import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Users, CreditCard, Key, Mail, BarChart3, Trash2, Search, RefreshCw } from "lucide-react";
import { adminAPI } from "../api";
import Card from "../components/atoms/Card";
import Chip from "../components/atoms/Chip";
import Button from "../components/atoms/Button";

const TABS = [
  { id: "overview", label: "Overview", Icon: BarChart3 },
  { id: "users",    label: "Users",    Icon: Users      },
  { id: "payments", label: "Payments", Icon: CreditCard },
  { id: "keys",     label: "API Keys", Icon: Key        },
  { id: "contacts", label: "Contacts", Icon: Mail       },
];

export default function Admin({ user }) {
  const [tab, setTab] = useState("overview");

  if (!user?.isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="page-enter" style={{ paddingBottom: 48, display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <Chip tone="ink">Admin</Chip>
        <h1 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 56px)", lineHeight: 1.05, fontWeight: 400, letterSpacing: "-0.025em", margin: "12px 0 8px" }}>
          The <em style={{ color: "var(--gold-500)" }}>back office.</em>
        </h1>
        <p style={{ color: "var(--fg-2)", fontSize: 16, lineHeight: 1.6, margin: 0 }}>
          Users, payments, API keys, contact messages — all in one place.
        </p>
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", borderBottom: "1px solid var(--border-1)", paddingBottom: 6 }}>
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button key={t.id} type="button" onClick={() => setTab(t.id)} style={{
              display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 16px",
              background: active ? "var(--ink-900)" : "transparent",
              color: active ? "var(--paper)" : "var(--fg-2)",
              border: 0, borderRadius: "var(--radius-sm)", fontWeight: 600, fontSize: 14, cursor: "pointer",
              transition: "all 150ms cubic-bezier(.2,.8,.2,1)",
            }}>
              <t.Icon size={15} strokeWidth={1.6} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <Overview />}
      {tab === "users"    && <UsersTab />}
      {tab === "payments" && <PaymentsTab />}
      {tab === "keys"     && <KeysTab />}
      {tab === "contacts" && <ContactsTab />}
    </div>
  );
}

// ============================================================
// Overview
// ============================================================

function Overview() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    adminAPI.overview().then(setData).catch((e) => setErr(e.message));
  }, []);

  if (err)   return <ErrorBox>{err}</ErrorBox>;
  if (!data) return <LoadingBox>Loading…</LoadingBox>;

  return (
    <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
      <Stat label="Users"            big={data.users.total}                hint={`+${data.users.newToday} today`} />
      <Stat label="Revenue this month" big={`₹${data.revenue.thisMonth.toLocaleString("en-IN")}`} hint={`${data.revenue.paymentsThisMonth} payments`} />
      <Stat label="API keys"         big={data.apiKeys.total}              hint={`${data.apiKeys.active} active`} />
      <Stat label="Contacts"         big={data.contacts.unread}            hint={`${data.contacts.total} total · unread`} />
    </div>
  );
}

function Stat({ label, big, hint }) {
  return (
    <Card paperDeep>
      <p className="eyebrow">{label}</p>
      <p className="font-display" style={{ fontSize: 40, lineHeight: 1, margin: "8px 0 4px", fontWeight: 400 }}>{big}</p>
      <p style={{ fontSize: 12, color: "var(--fg-3)", margin: 0 }}>{hint}</p>
    </Card>
  );
}

// ============================================================
// Users
// ============================================================

function UsersTab() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");

  function load() {
    setRows(null);
    adminAPI.listUsers(q).then((d) => setRows(d.users)).catch((e) => setErr(e.message));
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  async function handleDelete(id, email) {
    if (!window.confirm(`Permanently delete ${email}?`)) return;
    try { await adminAPI.deleteUser(id); load(); } catch (e) { alert(e.message); }
  }

  async function handleUpgrade(id) {
    try { await adminAPI.updateUser(id, { level: "Premium" }); load(); } catch (e) { alert(e.message); }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <div style={{ position: "relative", flex: 1, maxWidth: 360 }}>
          <Search size={15} strokeWidth={1.6} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--fg-3)" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search name or email…"
            style={{ width: "100%", padding: "8px 12px 8px 32px", border: "1px solid var(--border-2)", borderRadius: "var(--radius-sm)", fontSize: 14, fontFamily: "var(--font-body)" }} />
        </div>
        <Button variant="ghost" size="sm" onClick={load}><RefreshCw size={14} strokeWidth={1.6} /></Button>
      </div>
      {err && <ErrorBox>{err}</ErrorBox>}
      {!rows ? <LoadingBox>Loading…</LoadingBox> : (
        <Table headers={["ID", "Name", "Email", "Level", "Day", "Joined", ""]}>
          {rows.map((u) => (
            <tr key={u.id} style={trStyle}>
              <td style={tdStyle}>{u.id}</td>
              <td style={tdStyle}>{u.name}</td>
              <td style={tdStyle}>{u.email}</td>
              <td style={tdStyle}><Chip tone={u.level === "Premium" ? "gold" : "neutral"}>{u.level}</Chip></td>
              <td style={tdStyle}>{u.current_day}</td>
              <td style={{ ...tdStyle, fontSize: 12, color: "var(--fg-3)" }}>{u.joined_at?.split(" ")[0]}</td>
              <td style={{ ...tdStyle, textAlign: "right", display: "flex", gap: 6, justifyContent: "flex-end" }}>
                {u.level !== "Premium" && (
                  <button type="button" onClick={() => handleUpgrade(u.id)} style={btnGhost}>Upgrade</button>
                )}
                <button type="button" onClick={() => handleDelete(u.id, u.email)} style={btnDanger} aria-label="Delete">
                  <Trash2 size={13} strokeWidth={1.6} />
                </button>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

// ============================================================
// Payments
// ============================================================

function PaymentsTab() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [status, setStatus] = useState("");

  function load() {
    setRows(null);
    adminAPI.listPayments(status).then((d) => setRows(d.payments)).catch((e) => setErr(e.message));
  }
  useEffect(load, [status]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 6 }}>
        {["", "success", "pending", "failed"].map((s) => (
          <button key={s || "all"} type="button" onClick={() => setStatus(s)} style={{
            padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", borderRadius: 999,
            background: status === s ? "var(--ink-900)" : "#fff",
            color: status === s ? "var(--paper)" : "var(--fg-2)",
            border: `1px solid ${status === s ? "var(--ink-900)" : "var(--border-2)"}`,
          }}>{s || "all"}</button>
        ))}
      </div>
      {err && <ErrorBox>{err}</ErrorBox>}
      {!rows ? <LoadingBox>Loading…</LoadingBox> : rows.length === 0 ? <EmptyBox>No payments yet.</EmptyBox> : (
        <Table headers={["ID", "Email", "Plan", "Amount", "Status", "Razorpay order", "Date"]}>
          {rows.map((p) => (
            <tr key={p.id} style={trStyle}>
              <td style={tdStyle}>{p.id}</td>
              <td style={tdStyle}>{p.email}</td>
              <td style={tdStyle}><Chip tone="ink">{p.plan_id}</Chip></td>
              <td style={tdStyle}>₹{p.amount}</td>
              <td style={tdStyle}><Chip tone={p.status === "success" ? "sage" : p.status === "failed" ? "berry" : "gold"}>{p.status}</Chip></td>
              <td style={{ ...tdStyle, fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-3)" }}>{p.razorpay_order_id || "—"}</td>
              <td style={{ ...tdStyle, fontSize: 12, color: "var(--fg-3)" }}>{p.created_at?.split(" ")[0]}</td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

// ============================================================
// API keys
// ============================================================

function KeysTab() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);

  function load() {
    setRows(null);
    adminAPI.listApiKeys().then((d) => setRows(d.keys)).catch((e) => setErr(e.message));
  }
  useEffect(load, []);

  async function toggle(keyId, on) {
    try { await adminAPI.toggleApiKey(keyId, on); load(); } catch (e) { alert(e.message); }
  }

  if (err)   return <ErrorBox>{err}</ErrorBox>;
  if (!rows) return <LoadingBox>Loading…</LoadingBox>;
  if (rows.length === 0) return <EmptyBox>No API keys yet.</EmptyBox>;

  return (
    <Table headers={["Key", "Owner", "Plan", "Today / Limit", "Total", "Status", ""]}>
      {rows.map((k) => (
        <tr key={k.key_id} style={trStyle}>
          <td style={{ ...tdStyle, fontFamily: "var(--font-mono)", fontSize: 11 }}>{k.key_id.slice(0, 24)}…</td>
          <td style={tdStyle}>{k.name}<br /><span style={{ color: "var(--fg-3)", fontSize: 12 }}>{k.email}</span></td>
          <td style={tdStyle}><Chip tone="gold">{k.plan}</Chip></td>
          <td style={tdStyle}>{k.requests_today} / {k.daily_limit}</td>
          <td style={tdStyle}>{k.total_requests}</td>
          <td style={tdStyle}><Chip tone={k.active ? "sage" : "berry"}>{k.active ? "active" : "revoked"}</Chip></td>
          <td style={{ ...tdStyle, textAlign: "right" }}>
            <button type="button" onClick={() => toggle(k.key_id, !k.active)} style={k.active ? btnDanger : btnGhost}>
              {k.active ? "Revoke" : "Restore"}
            </button>
          </td>
        </tr>
      ))}
    </Table>
  );
}

// ============================================================
// Contacts
// ============================================================

function ContactsTab() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);

  function load() {
    setRows(null);
    adminAPI.listContacts().then((d) => setRows(d.contacts)).catch((e) => setErr(e.message));
  }
  useEffect(load, []);

  async function setStatus(id, status) {
    try { await adminAPI.updateContact(id, status); load(); } catch (e) { alert(e.message); }
  }

  if (err)   return <ErrorBox>{err}</ErrorBox>;
  if (!rows) return <LoadingBox>Loading…</LoadingBox>;
  if (rows.length === 0) return <EmptyBox>No contact messages yet.</EmptyBox>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {rows.map((c) => (
        <Card key={c.id} paperDeep>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
            <div>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>{c.subject}</p>
              <p style={{ margin: "4px 0 0", color: "var(--fg-3)", fontSize: 13 }}>
                {c.name} · <a href={`mailto:${c.email}`} style={{ color: "var(--gold-700)" }}>{c.email}</a> · {c.created_at?.split(" ")[0]}
              </p>
            </div>
            <Chip tone={c.status === "unread" ? "berry" : c.status === "replied" ? "sage" : "neutral"}>{c.status}</Chip>
          </div>
          <p style={{ marginTop: 12, lineHeight: 1.6, color: "var(--fg-1)", whiteSpace: "pre-wrap" }}>{c.message}</p>
          <div style={{ marginTop: 10, display: "flex", gap: 6 }}>
            {c.status !== "read"    && <button type="button" onClick={() => setStatus(c.id, "read")}    style={btnGhost}>Mark read</button>}
            {c.status !== "replied" && <button type="button" onClick={() => setStatus(c.id, "replied")} style={btnGhost}>Mark replied</button>}
            {c.status !== "unread"  && <button type="button" onClick={() => setStatus(c.id, "unread")}  style={btnGhost}>Mark unread</button>}
          </div>
        </Card>
      ))}
    </div>
  );
}

// ============================================================
// Shared bits
// ============================================================

function Table({ headers, children }) {
  return (
    <div style={{ overflowX: "auto", border: "1px solid var(--border-1)", borderRadius: "var(--radius-md)", background: "#fff" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "var(--font-body)" }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "var(--fg-3)", borderBottom: "1px solid var(--border-1)" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

const trStyle = { borderBottom: "1px solid var(--border-1)" };
const tdStyle = { padding: "10px 14px", fontSize: 14, color: "var(--fg-1)", verticalAlign: "middle" };
const btnGhost  = { padding: "5px 10px", fontSize: 12, fontWeight: 600, background: "#fff", color: "var(--fg-2)", border: "1px solid var(--border-2)", borderRadius: "var(--radius-sm)", cursor: "pointer" };
const btnDanger = { padding: "5px 10px", fontSize: 12, fontWeight: 600, background: "#fdf0f1", color: "var(--berry-dark)", border: "1px solid #ddb1b6", borderRadius: "var(--radius-sm)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 };

function ErrorBox({ children }) {
  return <div style={{ padding: 12, background: "#fdf0f1", border: "1px solid #ddb1b6", borderRadius: "var(--radius-sm)", color: "var(--berry-dark)", fontSize: 14 }}>{children}</div>;
}
function LoadingBox({ children }) {
  return <div style={{ padding: 24, color: "var(--fg-3)", fontSize: 14, textAlign: "center" }}>{children}</div>;
}
function EmptyBox({ children }) {
  return <div style={{ padding: 24, color: "var(--fg-3)", fontSize: 14, textAlign: "center", border: "1px dashed var(--border-2)", borderRadius: "var(--radius-md)" }}>{children}</div>;
}
